<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import { theme } from '@/event/theme/theme'
import type { Product } from '@/storage/products'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { formatPrice } from '@/event/locale'
import { getProductName } from '@/utils/product/getProductName'
import { hasDescriptionForLanguage } from '@/utils/product/hasDescriptionForLanguage'

const props = defineProps<{
    product: Product
    index: number
    truncatedDescriptions: string[]
    propsFunc: (el: HTMLDivElement, index: number) => void
}>()
</script>

<template>
    <section class="section-card-product-rect-happy">
        <img
            loading="lazy"
            :src="`${URL_IMAGE}${props.product.image}`"
            :alt="getProductName(props.product)"
            class="img-happy"
        />
        <svg
            class="svg-card-product-rect"
            :class="{
                'svg-card-product-rect-no-description': !hasDescriptionForLanguage(
                    props.product.description,
                    languageIdBus
                )
            }"
            viewBox="0 0 281 215"
            xmlns="http://www.w3.org/2000/svg"
        >
            <g
                :filter="
                    'url(#' +
                    (theme === 'happy-dark'
                        ? `filter0_d_dark${props.index}`
                        : `filter0_d_light${props.index}`) +
                    ')'
                "
            >
                <path
                    d="M10 189.666V51.3125C10 44.4222 10 40.9771 11.9991 38.7095C12.2208 38.4581 12.4581 38.2208 12.7095 37.9991C14.9771 36 18.438 36 25.36 36H255.64C262.562 36 266.023 36 268.29 37.9991C268.542 38.2208 268.779 38.4581 269.001 38.7095C271 40.9771 271 44.438 271 51.36V189.64C271 196.562 271 200.023 269.001 202.29C268.779 202.542 268.542 202.779 268.29 203.001C266.023 205 262.562 205 255.64 205H25.36C18.438 205 14.9771 205 12.7095 203.001C12.4581 202.779 12.2208 202.542 11.9991 202.29C10 200.023 10 196.571 10 189.666Z"
                />
            </g>

            <defs>
                <filter
                    :id="`filter0_d_light${props.index}`"
                    x="0"
                    y="0.449219"
                    filterUnits="userSpaceOnUse"
                    color-interpolation-filters="sRGB"
                >
                    <feDropShadow
                        dx="0"
                        dy="1"
                        stdDeviation="2"
                        flood-color="currentColor"
                        flood-opacity="0.7"
                    />
                </filter>

                <filter
                    :id="`filter0_d_dark${props.index}`"
                    x="0"
                    y="0.449219"
                    filterUnits="userSpaceOnUse"
                    color-interpolation-filters="sRGB"
                >
                    <feDropShadow
                        dx="0"
                        dy="1"
                        stdDeviation="2"
                        flood-color="currentColor"
                        flood-opacity="0.7"
                    />
                </filter>
            </defs>
        </svg>

        <div class="div-card-price-happy-rect">
            <svg
                class="svg-card-price-happy"
                viewBox="0 0 81 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g filter="url(#filter0_d_1017_6347)">
                    <path
                        d="M10 32.6401V23.3553C10 16.4365 10 12.9771 11.9991 10.7095C12.2208 10.4581 12.4581 10.2208 12.7095 9.99911C14.9771 8 18.438 8 25.36 8H55.64C62.562 8 66.0229 8 68.2905 9.99911C68.5419 10.2208 68.7792 10.4581 69.0009 10.7095C71 12.9771 71 16.438 71 23.36V32.64C71 39.562 71 43.0229 69.0009 45.2905C68.7792 45.5419 68.5419 45.7792 68.2905 46.0009C66.0229 48 62.562 48 55.64 48H25.36C18.438 48 14.9771 48 12.7095 46.0009C12.4581 45.7792 12.2208 45.5419 11.9991 45.2905C10 43.0229 10 39.562 10 32.6401Z"
                    />
                </g>
                <defs>
                    <filter
                        id="filter0_d_1017_6347"
                        x="0"
                        y="0"
                        width="81"
                        height="60"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                    >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="2" />
                        <feGaussianBlur stdDeviation="5" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_1017_6347"
                        />
                        <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_1017_6347"
                            result="shape"
                        />
                    </filter>
                </defs>
            </svg>

            <p class="p-card-price-happy">
                {{ formatPrice(props.product.price) }}
            </p>
        </div>

        <div
            class="div-card-description-happy"
            :ref="(el) => props.propsFunc(el as HTMLDivElement, index)"
        >
            <div class="div-card-name-happy">
                <h2 class="h2-card-name-happy">{{ getProductName(props.product) }}</h2>

                <p
                    class="p-card-name-happy"
                    v-if="
                        hasDescriptionForLanguage(
                            props.product.description,
                            languageIdBus
                        )
                    "
                >
                    {{ props.truncatedDescriptions[index] }}
                </p>
            </div>

            <button class="div-select-product-happy">
                <svg
                    class="svg-size-select-product-happy"
                    viewBox="0 0 18 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M8.99993 6.99976C9.17351 6.99976 9.31439 6.85718 9.31439 6.68157C9.31439 6.50593 9.17351 6.36338 8.99993 6.36338C8.82634 6.36338 8.68546 6.50593 8.68546 6.68157C8.68546 6.85718 8.82634 6.99976 8.99993 6.99976Z"
                    />
                    <path
                        d="M15.7923 4.45429H13.9327L11.0721 0.472049C10.8151 0.0264576 10.2491 -0.13214 9.79948 0.119802C9.34376 0.375971 9.17989 0.956793 9.43316 1.41805C9.44042 1.43109 9.44841 1.44365 9.45737 1.45558L11.7011 4.45429H6.29876L8.54248 1.45558C8.55131 1.44365 8.55943 1.43109 8.56669 1.41805C8.81936 0.95791 8.65732 0.376592 8.2 0.119553C7.75142 -0.131892 7.18539 0.0265819 6.92791 0.471801L4.06718 4.45429H2.20755C1.51402 4.45429 0.949707 5.02528 0.949707 5.72705C0.949707 6.28051 1.30471 6.76241 1.80477 6.93292C1.85808 6.9747 1.92466 6.99981 1.99713 6.99981C2.01163 6.99981 2.02526 7.00067 2.03951 7.00267C2.17488 7.02106 2.28212 7.12495 2.30668 7.26095L3.37647 13.2163C3.45815 13.6704 3.84853 14 4.30473 14H13.6951C14.1513 14 14.5417 13.6704 14.6234 13.2162L15.6932 7.26108C15.7177 7.12495 15.8251 7.02106 15.9609 7.00267C15.9747 7.00067 15.9883 6.99981 16.0027 6.99981C16.0757 6.99981 16.1428 6.97445 16.1961 6.93219C16.6965 6.76053 17.0501 6.27952 17.0501 5.72705C17.0501 5.02528 16.4858 4.45429 15.7923 4.45429ZM9.97438 1.09227C9.90253 0.941752 9.95831 0.75842 10.1045 0.676388C10.2567 0.590998 10.4485 0.646058 10.5324 0.799063C10.5386 0.810499 10.5456 0.821438 10.5531 0.831878L13.1551 4.45429H12.4898L9.97438 1.09227ZM7.44677 0.832002C7.45428 0.821562 7.46101 0.810747 7.4673 0.799562C7.55142 0.646433 7.74343 0.591246 7.895 0.676139C8.04145 0.75842 8.09745 0.941504 8.02547 1.09227L5.51003 4.45429H4.84472L7.44677 0.832002ZM15.7923 6.36343H10.2578C10.0841 6.36343 9.94331 6.50585 9.94331 6.68162C9.94331 6.85736 10.0841 6.99656 10.2578 6.99656H15.113C15.0966 7.04404 15.0835 7.09326 15.0744 7.14398L14.0045 13.1024C13.9774 13.2537 13.8472 13.3636 13.6951 13.3636H4.30473C4.15256 13.3636 4.02246 13.2537 3.9952 13.1025L2.92541 7.1471C2.91632 7.09638 2.9033 7.04716 2.88684 6.99981H7.74208C7.91576 6.99981 8.05654 6.85736 8.05654 6.68162C8.05654 6.50585 7.91576 6.36343 7.74208 6.36343H2.20755C1.85784 6.36343 1.57863 6.07505 1.57863 5.72705C1.57863 5.37615 1.86079 5.09067 2.20755 5.09067H15.7923C16.1391 5.09067 16.4212 5.37615 16.4212 5.72705C16.4212 6.0804 16.1355 6.36343 15.7923 6.36343Z"
                    />
                    <path
                        d="M8.99993 7.95433C8.47971 7.95433 8.05654 8.38252 8.05654 8.90891V11.4544C8.05654 11.9808 8.47971 12.409 8.99993 12.409C9.52014 12.409 9.94331 11.9808 9.94331 11.4544V8.90891C9.94331 8.38154 9.52112 7.95433 8.99993 7.95433ZM9.31439 11.4544C9.31439 11.6299 9.17338 11.7726 8.99993 11.7726C8.82647 11.7726 8.68546 11.6299 8.68546 11.4544V8.90891C8.68546 8.73339 8.82647 8.59071 8.99993 8.59071C9.17351 8.59071 9.31439 8.73326 9.31439 8.90891V11.4544Z"
                    />
                    <path
                        d="M12.1445 12.409C12.6648 12.409 13.0879 11.9808 13.0879 11.4544V8.90891C13.0879 8.38154 12.6657 7.95433 12.1445 7.95433C11.6243 7.95433 11.2012 8.38252 11.2012 8.90891V11.4544C11.2012 11.9808 11.6243 12.409 12.1445 12.409ZM11.8301 8.90891C11.8301 8.73339 11.9711 8.59071 12.1445 8.59071C12.3181 8.59071 12.459 8.73326 12.459 8.90891V11.4544C12.459 11.6299 12.318 11.7726 12.1445 11.7726C11.9711 11.7726 11.8301 11.6299 11.8301 11.4544V8.90891Z"
                    />
                    <path
                        d="M5.85531 7.95433C5.3351 7.95433 4.91192 8.38252 4.91192 8.90891V11.4544C4.91192 11.9808 5.3351 12.409 5.85531 12.409C6.37552 12.409 6.79869 11.9808 6.79869 11.4544V8.90891C6.79869 8.38154 6.3765 7.95433 5.85531 7.95433ZM6.16977 11.4544C6.16977 11.6299 6.02877 11.7726 5.85531 11.7726C5.68185 11.7726 5.54085 11.6299 5.54085 11.4544V8.90891C5.54085 8.73339 5.68185 8.59071 5.85531 8.59071C6.02889 8.59071 6.16977 8.73314 6.16977 8.90891V11.4544Z"
                    />
                </svg>
            </button>
        </div>
    </section>
</template>

<style scoped>
.section-card-product-rect-happy {
    @apply relative flex;
}

.section-card-product-rect-happy .svg-card-product-rect {
    /* refactored */
    @apply fill-t-card text-t-text;
}

.img-happy {
    @apply absolute inset-0 -top-6 left-2 h-3/5 object-contain rounded-xl z-10;
}
.svg-card-product-rect {
    @apply w-full h-auto;
    /* refactored */
    @apply fill-t-card;
}

.svg-card-product-rect-no-description {
    @apply w-full h-auto;
}

.div-card-price-happy-rect {
    @apply absolute z-10 top-[20%] right-[3%] flex items-center justify-center;
}

.svg-card-price-happy {
    /* refactored */
    @apply w-[4.5rem] fill-t-card;
}

.p-card-price-happy {
    @apply absolute text-sm inset-0 flex items-center justify-center text-center m-auto font-semibold;
    /* refactored */
    @apply text-t-text;
}

.div-card-description-happy {
    @apply absolute top-[45%] bottom-3 transform text-center min-w-full text-sm;
    @apply cursor-pointer font-semibold flex flex-col items-start pl-4;
    /* refactored */
    @apply text-t-text
    /* z-index: 10; */;
}
.div-card-name-happy {
    @apply overflow-hidden w-[97%];
    /* @apply happy-gray-sand:text-bg-happy-light; */
}

.h2-card-name-happy {
    @apply text-wrap text-start w-3/5;
    text-overflow: ellipsis;
}
.p-card-name-happy {
    @apply font-normal text-start text-xs w-4/6 overflow-hidden;
    transition: all 0.3s ease;
}

.div-select-product-happy {
    @apply absolute right-[7%] top-6 w-8 h-7 border-2 flex items-center justify-center rounded-lg;
    /* refactored */
    @apply bg-t-accent border-t-accent;
    @apply happy-ifo-dark:bg-t-bg happy-ifo-dark:border-t-border;
}

.svg-size-select-product-happy {
    /* refactored */
    @apply w-5 h-5 fill-t-on-accent;
    @apply happy-ifo-dark:fill-t-on-accent;
}

/* Active */

.section-card-product-rect-happy:active .svg-card-product-rect,
.section-card-product-rect-happy:focus .svg-card-product-rect {
    /* refactored */
    @apply fill-t-accent;
}

.section-card-product-rect-happy:active .p-card-price-happy,
.section-card-product-rect-happy:focus .p-card-price-happy {
    /* refactored */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

.section-card-product-rect-happy:active .div-card-description-happy,
.section-card-product-rect-happy:focus .div-card-description-happy {
    /* refactored */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

.section-card-product-rect-happy:active .svg-card-price-happy,
.section-card-product-rect-happy:focus .svg-card-price-happy {
    /* refactored */
    @apply fill-t-card;
}

.section-card-product-rect-happy:active .div-select-product-happy,
.section-card-product-rect-happy:focus .div-select-product-happy {
    /* refactored */
    @apply bg-t-accent-hover border-t-border;
    @apply happy-ifo-dark:bg-t-card happy-ifo-dark:border-t-border;
}

.section-card-product-rect-happy:active .svg-size-select-product-happy,
.section-card-product-rect-happy:focus .svg-size-select-product-happy {
    /* refactored */
    @apply fill-t-on-accent;
    @apply happy-ifo-dark:fill-t-on-accent;
}

/* Hover */

@media (hover: hover) {
    .section-card-product-rect-happy:hover .svg-card-product-rect {
        /* refactored */
        @apply fill-t-accent-hover;
    }

    .section-card-product-rect-happy:hover .p-card-price-happy {
        /* refactored */
        @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
    }

    .section-card-product-rect-happy:hover .div-card-description-happy {
        /* refactored */
        @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
    }

    .section-card-product-rect-happy:hover .svg-card-price-happy {
        /* refactored */
        @apply fill-t-card;
    }
    .section-card-product-rect-happy:hover .div-select-product-happy {
        /* refactored */
        @apply bg-t-accent-hover border-t-border;
        @apply happy-ifo-dark:bg-t-card happy-ifo-dark:border-t-border;
    }

    .section-card-product-rect-happy:hover .svg-size-select-product-happy {
        /* refactored */
        @apply fill-t-on-accent;
        @apply happy-ifo-dark:fill-t-on-accent;
    }
}

@media screen and (min-width: 370px) {
    .div-select-product-happy {
        @apply w-9 h-8 top-8;
    }
    .svg-size-select-product-happy {
        @apply w-6 h-6;
    }
    .div-card-price-happy-rect {
        @apply top-[30%]  right-[4%]  flex items-center justify-center;
    }
    .img-happy-no-description {
        @apply -top-2;
    }
}
</style>
